import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSourceSystem } from "../../hooks/useSourceSystem";
import {
  getAppraisalData,
  getFirstQuestionOfCategoryId,
  getQuestionsByCategory,
  getSourceSystemContent,
  selectImageUploadData
} from "../../store/selectors";
import { appraisalSlice } from "../../store/slices/Appraisal";
import {
  getDefaultAnswers,
  getPhotoQuantityMultipleChoice,
  getPrePopulatedOrUpdatedAnswers,
  hasAnswersChanged,
  isQuestionsAnswered
} from "../helpers";
import { QuestionIds, yesMaskValue } from "../../data/QuestionPresentation";
import { useNavigate } from "react-router-dom";
import {
  ConditionalQuestionsFromAssumptionsIds,
  MultipleChoiceQuestionIds
} from "../../data/QuestionData";
import {
  getInitialCheckboxState,
  validateCheckboxAnswers
} from "../../components/AssumptionQuestions/helpers";
import { imageUploadSlice } from "../../store/slices/ImageUploads";
import { allRequiredPhotosUploaded } from "../ImageUploads/helpers";

export const useAssumptions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedQuestionsHaveChanged, setSelectedQuestionsHaveChanged] =
    useState({});
  const [answersChanged, setAnswersChanged] = useState(false);
  const [glassAnswerImagesIncreased, setGlassAnswerImagesIncreased] =
    useState(false);
  const {
    sourceSystem,
    sourceSystem: {
      photosRequired,
      showAssumptionQuestionsWithCondition,
      analyticSearchParam
    }
  } = useSourceSystem();
  const {
    questionsAndAnswers,
    fullValuation,
    allQuestionsAnswered,
    questions
  } = useSelector(getAppraisalData);
  const {
    quickValuation,
    initialOfferContent: { stepNumber }
  } = useSelector(getSourceSystemContent);
  const currentAnswersWithDefaults = getPrePopulatedOrUpdatedAnswers(
    questions,
    questionsAndAnswers
  );
  const [defaultAnswers] = useState(currentAnswersWithDefaults);
  const factualQuestions = useSelector(getQuestionsByCategory("Factual"));
  const conditionQuestions = useSelector(getQuestionsByCategory("Condition"));
  const firstConditionQuestion = useSelector(
    getFirstQuestionOfCategoryId("Condition")
  );
  const { images, requiredPhotoData } = useSelector(selectImageUploadData);
  const allImagesUploaded = allRequiredPhotosUploaded(
    images,
    requiredPhotoData
  );

  const [checkboxState, setCheckboxState] = useState(
    getInitialCheckboxState(MultipleChoiceQuestionIds, questionsAndAnswers)
  );
  const assumptionQuestions = useSelector(getQuestionsByCategory("Assumption"));
  const glassQuestion = assumptionQuestions.find(
    (question) => question.id === QuestionIds.GLASS
  );
  const checkBoxQuestionsIncluded = assumptionQuestions?.some((question) =>
    MultipleChoiceQuestionIds.includes(question.id)
  );
  const glassNoDamageAnswerId =
    glassQuestion?.answers[glassQuestion.answers.length - 1].id;
  const showGlassPhotoIncreaseWarning =
    glassAnswerImagesIncreased && photosRequired;

  const allQuestions = questions.map((q) => q.questions).flat();

  const answersComplete = isQuestionsAnswered(
    allQuestions,
    questionsAndAnswers
  );

  // handles back navigation after removing answers for full valuation
  useEffect(() => {
    const defaultCondition = getDefaultAnswers(conditionQuestions);
    const defaultFactual = getDefaultAnswers(factualQuestions);
    const testId = Object.keys(defaultCondition)[0];

    if (!questionsAndAnswers.hasOwnProperty(testId)) {
      dispatch(
        appraisalSlice.actions.setQuestionsAndAnswers({
          ...defaultCondition,
          ...defaultFactual
        })
      );
    }
    if (!answersComplete) {
      dispatch(
        appraisalSlice.actions.setQuestionsAndAnswers(
          currentAnswersWithDefaults
        )
      );
      dispatch(appraisalSlice.actions.setAnswersChanged(true));
    }
  }, [conditionQuestions, dispatch, factualQuestions]);

  const handleChange = (questionId, value, showCheckboxValue) => {
    setSelectedQuestionsHaveChanged({
      ...selectedQuestionsHaveChanged,
      [questionId]: [value]
    });
    if (MultipleChoiceQuestionIds?.includes(questionId)) {
      dispatch(
        appraisalSlice.actions.updateConditionalAnswersSubmitted(questionId)
      );
      setCheckboxState({
        ...checkboxState,
        [questionId]: [value]
      });
    }
  };

  const handleCheckboxChange = (questionId, answerId, checked) => {
    dispatch(
      appraisalSlice.actions.updateConditionalAnswersSubmitted(questionId)
    );
    if (checkboxState[questionId][0] === yesMaskValue) {
      setSelectedQuestionsHaveChanged({
        ...selectedQuestionsHaveChanged,
        [questionId]: [answerId]
      });
      setCheckboxState({
        ...checkboxState,
        [questionId]: [answerId]
      });
      return;
    }
    if (checked) {
      const checkboxAnswerAdded = [...checkboxState[questionId], answerId];
      setCheckboxState({
        ...checkboxState,
        [questionId]: checkboxAnswerAdded
      });
      setSelectedQuestionsHaveChanged({
        ...selectedQuestionsHaveChanged,
        [questionId]: checkboxAnswerAdded
      });
    } else {
      const checkboxAnswerRemoved = [...checkboxState[questionId]].filter(
        (answer) => answer !== answerId
      );
      setSelectedQuestionsHaveChanged({
        ...selectedQuestionsHaveChanged,
        [questionId]: checkboxAnswerRemoved
      });
      setCheckboxState({
        ...checkboxState,
        [questionId]: checkboxAnswerRemoved
      });
    }
  };

  const hasGlassImagesRequiredIncreased = (
    newGlassAnswer,
    initialGlassAnswer
  ) => {
    if (newGlassAnswer !== initialGlassAnswer) {
      if (newGlassAnswer[0] === glassNoDamageAnswerId) {
        setGlassAnswerImagesIncreased(false);
        return;
      }
      if (
        initialGlassAnswer[0] === glassNoDamageAnswerId &&
        !newGlassAnswer?.includes(glassNoDamageAnswerId) &&
        newGlassAnswer.length > 0
      ) {
        setGlassAnswerImagesIncreased(true);
        return;
      }
      if (newGlassAnswer?.length > initialGlassAnswer?.length) {
        setGlassAnswerImagesIncreased(true);
        return;
      }
    } else {
      setGlassAnswerImagesIncreased(false);
    }
  };

  useEffect(() => {
    const combinedNewAnswers = checkBoxQuestionsIncluded
      ? {
          ...questionsAndAnswers,
          ...selectedQuestionsHaveChanged,
          ...checkboxState
        }
      : { ...questionsAndAnswers, ...selectedQuestionsHaveChanged };
    const answersChanged = hasAnswersChanged(
      defaultAnswers,
      combinedNewAnswers
    );
    hasGlassImagesRequiredIncreased(
      combinedNewAnswers[QuestionIds.GLASS],
      defaultAnswers[QuestionIds.GLASS]
    );
    setAnswersChanged(answersChanged);
  }, [selectedQuestionsHaveChanged, defaultAnswers, questionsAndAnswers]);

  const handleSubmit = async () => {
    dispatch(
      appraisalSlice.actions.setConditionalAnswersSubmitted([
        QuestionIds.MISSINGITEMS,
        QuestionIds.GLASS
      ])
    );
    if (checkBoxQuestionsIncluded && !validateCheckboxAnswers(checkboxState)) {
      window.scrollTo({ top: 500, behavior: "smooth" });
      return;
    }
    if (
      glassQuestion &&
      ((photosRequired && glassAnswerImagesIncreased) || !allImagesUploaded)
    ) {
      const photoQuantityForGlass = getPhotoQuantityMultipleChoice(
        glassQuestion.answers,
        checkboxState[QuestionIds.GLASS]
      );
      dispatch(
        appraisalSlice.actions.setPhotosRequired({
          [QuestionIds.GLASS]: photoQuantityForGlass
        })
      );
    }
    await updateQuestionAnswers();
    if (sourceSystem.name === "quotations") {
      sourceSystem.handleAssumptionsOnSubmit(allQuestionsAnswered);
    }
    if (sourceSystem.name === "value-my-car") {
      if (
        (glassAnswerImagesIncreased && allQuestionsAnswered) ||
        !allImagesUploaded
      ) {
        dispatch(imageUploadSlice.actions.setReturnToGallery(false));
        return navigate("/photos-intro");
      } else {
        sourceSystem.handleAssumptionsOnSubmit(allQuestionsAnswered);
      }
    }
    sourceSystem.handleAssumptionsOnSubmit(fullValuation);
  };

  const quickValuationQuestions = assumptionQuestions.concat(factualQuestions);
  const assumptionsQuestionsToRender = !showAssumptionQuestionsWithCondition
    ? assumptionQuestions
    : assumptionQuestions.filter(
        (question) =>
          !ConditionalQuestionsFromAssumptionsIds.includes(question.id)
      );
  const updateQuestionAnswers = async () => {
    const questionsToUpdate = checkBoxQuestionsIncluded
      ? { ...selectedQuestionsHaveChanged, ...checkboxState }
      : { ...selectedQuestionsHaveChanged };
    await dispatch(
      appraisalSlice.actions.setQuestionsAndAnswers(questionsToUpdate)
    );
    if (answersChanged) {
      dispatch(appraisalSlice.actions.setAnswersChanged(true));
    }
  };
  return {
    stepNumber,
    quickValuation,
    fullValuation,
    quickValuationQuestions,
    firstConditionQuestion,
    assumptionQuestions: assumptionsQuestionsToRender,
    answersChanged,
    handleSubmit,
    handleChange,
    handleCheckboxChange,
    selectedQuestionsHaveChanged,
    updateQuestionAnswers,
    showGlassPhotoIncreaseWarning,
    checkboxState,
    analyticSearchParam
  };
};
